import { useIntl } from '@/i18n';
import { isChunkLoadError, reloadNow } from '@/utils/chunkRecovery';
import { Button, Result } from 'antd';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';

/**
 * 路由级兜底页（`errorElement`）：渲染期抛错、页面 chunk 加载失败都在这里收口。
 *
 * 为什么要有这一页：不加 `errorElement` 时 react-router 会渲染它自带的那张
 * "Unexpected Application Error!" + 英文堆栈页（2026-10-21 开发环境实测，
 * 换版后旧标签页登录就撞上），对使用者既看不懂也没出口。
 *
 * 两种情形分开说：
 * - **chunk 加载失败**：几乎都是部署换版导致的（`utils/chunkRecovery.ts` 有完整说明），
 *   入口里已经自动刷新过一次；还停在这里说明刷新没成功（冷却窗口内重复失败、
 *   或产物真的缺文件）。文案说"版本已更新"并给"重新载入"，是能立刻解决问题的动作。
 * - **其它错误**：页面自身的 bug，文案只承诺"重新载入/稍后再试"，不谎称已修复。
 */
export default function RouteFailure() {
  const intl = useIntl();
  const error = useRouteError();
  const staleBuild = isChunkLoadError(error);
  const t = (id: string) => intl.formatMessage({ id });

  useEffect(() => {
    // 兜底页已经吃掉了错误，不打日志的话线上只剩"用户说白屏了"这一条线索
    console.error('[routeFailure] 路由渲染失败', error);
  }, [error]);

  return (
    <Result
      status={staleBuild ? 'info' : 'error'}
      title={t(
        staleBuild ? 'routeFailure.staleBuildTitle' : 'routeFailure.title',
      )}
      subTitle={t(
        staleBuild ? 'routeFailure.staleBuildTip' : 'routeFailure.tip',
      )}
      extra={
        <Button type="primary" onClick={reloadNow}>
          {t('routeFailure.reload')}
        </Button>
      }
    />
  );
}
